<template>
  <div
    v-show="showScrollButton"
    class="scroll-to-top-button critical-component-clear"
  >
    <btn
      class="scroll-to-top-button__button"
      show-icon
      type="button"
      size="medium"
      url="#main-content"
      modifiers="secondary"
      label="Scroll to top"
      @click="scrollToTop"
    >
      <template #icon>
        <icon-chevron-up />
      </template>
    </btn>
  </div>
</template>

<script>
import Btn from '~global/btn/btn.vue';
import IconChevronUp from '~icons/directional-navigation/chevron/up.svg';

import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

/**
 * Vue: Scroll to top button (scroll-to-top-button)
 * -----------------------------------------------------------------------------
 * A arrow up button is shown, if a certain element or px bypassed and scrolls
 * the user to the top of the page on click.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
export default {
  name: 'ScrollToTopButton',
  components: { Btn, IconChevronUp },

  props: {
    // Threshold for the scroll position to show the button on mobile
    mobileThreshold: {
      type: Number,
      default: 768,
    },
    // Threshold for the scroll position to show the button on desktop
    desktopThreshold: {
      type: Number,
      default: 1024,
    },
  },

  data() {
    return {
      // Controls the visibility of the button
      showScrollButton: false,
      // Change this value to the pixel threshold
      scrollThreshold: 300,
      // To track if the intersection element is visible
      isIntersecting: false,
      // Previous scroll position to determine scroll direction
      lastScrollY: 0,
      // Animation frame reference
      scrollAnimation: null,
      // Start time of animation
      animationStartTime: null,
      // Duration of animation in milliseconds
      animationDuration: 400,
      // Initial scroll position when animation starts
      initialScrollPosition: null,
      // check if add to cart is sticky
      isAddToCartSticky: false,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
  },

  watch: {
    isIntersecting() {
      const scrollingDown = window.scrollY > this.lastScrollY;
      this.updateButtonVisibility(scrollingDown);
    },
  },

  mounted() {
    // Add scroll event listener when component is mounted
    window.addEventListener('scroll', this.checkScrollDirection);

    // Listen for the event bus event
    cnvs.EventBus.listen('updateSticky', this.handleStickyUpdate);

    this.scrollThreshold = this.isMobile
      ? this.mobileThreshold
      : this.desktopThreshold;
  },

  beforeUnmount() {
    // Remove scroll event listener before component is unmounted
    window.removeEventListener('scroll', this.checkScrollDirection);

    // Remove event bus listener
    cnvs.EventBus.remove('updateSticky');
  },
  methods: {
    /**
     * Handles the sticky update event from the event bus
     * checks if the 'add to cart' is sticky and updates the visibility
     * Checks the current scroll position and updates the visibility of
     * the scroll-to-top button based on the threshold.
     * @param {Boolean} isSticky - Indicates if add to cart is visible.
     */
    handleStickyUpdate(isSticky) {
      this.isAddToCartSticky = isSticky;

      if (!isSticky) {
        this.showScrollButton = window.scrollY > this.scrollThreshold;
      }
    },

    /**
     * Checks the scroll direction and updates the visibility of
     * the scroll-to-top button.
     */
    checkScrollDirection() {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > this.lastScrollY;

      if (this.isAddToCartSticky) {
        this.updateButtonVisibility(scrollingDown);
      } else {
        this.showScrollButton =
          !scrollingDown && currentScrollY > this.scrollThreshold;
      }

      this.lastScrollY = currentScrollY;
    },

    /**
     * Updates the visibility of the scroll-to-top button based on the
     * intersection state and scroll direction.
     * @param {boolean} scrollingDown - Indicates if the user is scrolling down.
     */
    updateButtonVisibility(scrollingDown) {
      if (this.isAddToCartSticky) {
        this.showScrollButton = false;
        return;
      }

      if (this.isIntersecting) {
        this.showScrollButton = false;
      } else {
        this.showScrollButton = !scrollingDown;
      }
    },

    /**
     * Smoothly scrolls the window back to the top with an easing animation.
     */
    scrollToTop() {
      // Clear any existing animation frame
      cancelAnimationFrame(this.scrollAnimation);

      // Start animation
      this.animationStartTime = performance.now();
      this.animationStartScrollPosition = window.scrollY;
      this.scrollAnimation = requestAnimationFrame(this.animateScroll);
    },

    /**
     * Animation function for smooth scrolling.
     * @param {DOMHighResTimeStamp} currentTime - The current time when
     * the animation frame is fired.
     */
    animateScroll(currentTime) {
      const elapsed = currentTime - this.animationStartTime;

      // Calculate progress using easing function (easeInOutCubic)
      const progress = elapsed / this.animationDuration;
      const easedProgress = this.easeOutCirc(Math.min(progress, 1));

      // Calculate new scroll position
      const newScrollPosition =
        this.animationStartScrollPosition * (1 - easedProgress);

      // Apply new scroll position
      window.scrollTo(0, newScrollPosition);

      // Continue animation until duration is reached
      if (elapsed < this.animationDuration) {
        this.scrollAnimation = requestAnimationFrame(this.animateScroll);
      }
    },

    /**
     * Easing function for smooth scroll animation (circ ease-out).
     * @param {number} timeFactor - Time factor between 0 and 1.
     * @returns {number} - Eased value.
     */
    easeOutCirc(timeFactor) {
      const adjustedTime = timeFactor - 1;
      return Math.sqrt(1 - adjustedTime ** 2);
    },
  },
};
</script>

<style lang="scss">
@import './scroll-to-top-button';
</style>
